import type { FC } from 'react';
import React, { useState, useEffect } from 'react';

import type { DialogProps } from '@sravni/react-design-system/dist/types/components/Dialog';
import { Dialog } from '@sravni/react-design-system/lib/Dialog';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { RadioButton } from '@sravni/react-design-system/lib/RadioButton';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Text } from '@sravni/react-design-system/lib/Typography';
import { ShieldSafety } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import AuthFormFOS from '@src/components/form/AuthFormFOS';
import { sendEvent } from '@src/helpers/analyticsEvents';
import { save } from '@src/services/formbuilder';
import { useCardClick } from 'entities/Microcredits/lib/useCardClick';

import style from './BankruptcyDialog.module.scss';

interface Props extends Pick<DialogProps, 'visible' | 'onClose'> {
  data: { offer: IClientMicrocreditListItem; position: number };
}

const eventCategory = 'Банкротство';

type DebtType = 'LOW_DEBT' | 'HIGH_DEBT';

const options: Array<{ label: string; value: DebtType }> = [
  {
    label: 'Долг до 300 000 ₽',
    value: 'LOW_DEBT',
  },
  {
    label: 'Долг от 300 000 ₽',
    value: 'HIGH_DEBT',
  },
];

const configs = {
  // ФЦБГ
  fcbg: {
    productId: '63ef8a1e3857ac0027eccc01',
    bankId: '1000212',
    name: 'Федеральный центр банкротства граждан',
    eventLabel: 'Федеральный центр банкротства граждан',
    title: 'нашего официального партнера Федерального Центра Банкротства Граждан (ФЦБГ)',
  },
  // НССД
  nssd: {
    productId: '64afd504ee273100272a94b7',
    bankId: '7778279',
    name: 'Национальная Служба Списания Долгов (НССД)',
    eventLabel: 'Национальная Служба Списания Долгов',
    title: 'Национальной Службы Списания Долгов (НССД)',
    utm_source: 'sravni',
    utm_campaign: 'seosravni',
  },
  // ЮС-Альянс
  'yus-alyans': {
    productId: '67ab264009a2c6002b9d8d68',
    bankId: '7779699',
    name: 'ЮС Альянс',
    eventLabel: 'ЮС Альянс',
    title: 'ЮС Альянс',
    utm_source: 'sravni',
    utm_campaign: 'mfo',
  },
};

export const BankruptcyDialog: FC<Props> = ({ visible, onClose, data: { offer, position } }) => {
  const isMobile = useIsMobile();
  const [debtValue, setDebtValue] = useState<DebtType>('LOW_DEBT');
  const { cardLink } = useCardClick({
    offer,
    position,
  });

  const cardConfig = debtValue === 'LOW_DEBT' ? configs.nssd : configs['yus-alyans'];
  const eventLabel = `${cardConfig.eventLabel}|Банкротство`;

  useEffect(() => {
    sendEvent(eventCategory, 'Переход к оформлению заявки', eventLabel);
  }, [eventLabel]);

  const handleOnLogin = async (userData: { name: string; phone: string }) => {
    await save({
      ...userData,
      formId: 278,
      ...(debtValue === 'HIGH_DEBT' && { mfo_offer: true }),
      ProductType: 'МФО',
      productName: 'Списание долгов',

      BankId: cardConfig.bankId,
      BankName: cardConfig.name,
      offers: [
        {
          BankId: cardConfig.bankId,
          BankName: cardConfig.name,
          productName: 'Списание долгов',
          product_id: cardConfig.productId,
        },
      ],
      ...(debtValue === 'LOW_DEBT' && {
        utm: 'utmcsr=not_set|utmccn=not_set|utmcmd=organic|utmctr=not_set|utmcct=bankrotstvo_banner',
      }),
    });

    sendEvent(eventCategory, 'Отправить заявку', eventLabel);

    onClose(null);
  };

  const handleSend = () => {
    sendEvent(eventCategory, 'Подтвердить телефон', eventLabel);
  };

  return (
    <div className={style.cardWrapper}>
      <Dialog className={style.dialog} visible={visible} onClose={onClose} fullscreen={isMobile}>
        <Dialog.Header title={<>Бесплатная консультация по списанию долгов</>} className={style.dialogHeader} />
        <Dialog.Content>
          <Space direction="vertical" size={16}>
            <Space className={style.notion} direction="horizontal" size={12}>
              <div className={style.iconWrapper}>
                <Icon color="green" size={24} icon={<ShieldSafety />} />
              </div>
              <Text size={14}>
                Эксперт по банкротству компании <span className="bold">ЮС&nbsp;Альянс</span> оценит вашу финансовую
                ситуацию и предложит законное решение для списания долгов и защиты имущества
              </Text>
            </Space>
            <AuthFormFOS
              source="mfo"
              onLogin={handleOnLogin}
              onSend={handleSend}
              fields={['name', 'phone']}
              btnColor="dark"
              btnText="Отправить"
            >
              <RadioButton
                value={debtValue}
                onChange={(value) => setDebtValue(value as DebtType)}
                options={options}
                size={52}
              />
            </AuthFormFOS>
          </Space>
        </Dialog.Content>
      </Dialog>
      <iframe src={cardLink.href} className={style.iframe} title="pixel_visit" />
    </div>
  );
};
